import { Injectable } from '@angular/core';
import { BusinessObjectType } from '@core/enums/business-object-type.enum';
import { DictionarySet } from '@core/models/dictionary.model';
import { EventDto } from '@core/models/planner-models/event.dto';
import { PlannerRouteDto } from '@core/models/planner-models/planner.model';
import { ScheduleAbsenceDto } from '@core/models/scheduling/schedule-absence.dto';
import { addDays, changeDateKeepTime, differenceInDays, isSameDay, isBeforeDay, isAfterDay } from '@core/utils/date.utils';
import { cloneDeep } from 'lodash';
import { DaysModeEnum, PeriodsOffsetMap } from '../../_enums/days-mode.enum';
import { PlannerViewComponent } from '../components/planner-view/planner-view.component';
import { mapCalendarEventDtoToModel, mapScheduleAbsenceDtoToModel } from '../models/absence.model';
import { DayModel } from '../models/day-model';
import { GridRowModel } from '../models/grid-row-model';
import { OrderDroppedEvent } from '../models/order-drag-and-drop.model';
import { PlannerConfiguration } from '../models/planner-configuration.model';
import { mapRouteDtoToModel, RouteModel } from '../models/route.model';



@Injectable()
export class PlannerManagerService {

  private taskOrderTypeSet?: DictionarySet;
  private orderStatusSet?: DictionarySet;
  private vehicleCategorySet?: DictionarySet;

  private theMostUpToDateLastRoutes?: PlannerRouteDto[];
  private theMostUpToDateLastCalendarsEvents?: EventDto[];
  private theMostUpToDateLastAbsences?: ScheduleAbsenceDto[];



  constructor() { }

  public init(
    config: {
      taskOrderTypeSet?: DictionarySet,
      orderStatusSet?: DictionarySet,
      vehicleCategorySet?: DictionarySet
    }
  ) {
    if (config.orderStatusSet) {
      this.orderStatusSet = config.orderStatusSet;
    }
    if (config.vehicleCategorySet) {
      this.vehicleCategorySet = config.vehicleCategorySet;
    }
    if (config.taskOrderTypeSet) {
      this.taskOrderTypeSet = config.taskOrderTypeSet;
    }
  }

  public validateRoutesAndAbsencesConflicts(dropedEvent: OrderDroppedEvent, row: GridRowModel): 'ROUTE_CONFLICT' | 'ABSENCES_CONFLICT' | 'BOTH' | 'NONE' {
    const weekRoutes = row.days.flatMap(d => d.routes).filter(r => r.routeId !== dropedEvent.dragInfo.route?.routeId);
    const weekAbsences = row.days.flatMap(d => d.absences).filter(a => a !== undefined);

    const day = dropedEvent.dropInfo.day;

    if (day) {

      const diffInDays = differenceInDays(dropedEvent.dragInfo.order.StartDate, dropedEvent.dragInfo.order.EndDate);
      const newDates = {
        startDate: changeDateKeepTime(dropedEvent.dragInfo.order.StartDate, day.date),
        endDate: addDays(changeDateKeepTime(dropedEvent.dragInfo.order.EndDate, day.date), diffInDays),
      }


      let anyRouteWithConflicts = false;
      let anyAbsenceWithConflicts = false;
      if (weekRoutes.length) {
        anyRouteWithConflicts = weekRoutes.some(r =>
          r.PlanTime <= newDates.endDate && newDates.startDate <= r.PlanEndTime);
      }

      if (weekAbsences.length) {
        anyAbsenceWithConflicts = weekAbsences.some(a =>
          a && (
            isSameDay(a.dateFrom, newDates.startDate)
            || isSameDay(a.dateFrom, newDates.endDate)
            || (a.dateTo && (a.dateFrom < newDates.endDate && newDates.startDate < a.dateTo))));
      }

      if (anyAbsenceWithConflicts && anyRouteWithConflicts) {
        return 'BOTH';
      }

      if (anyAbsenceWithConflicts) {
        return 'ABSENCES_CONFLICT';
      }

      if (anyRouteWithConflicts) {
        return 'ROUTE_CONFLICT';
      }
    }

    return 'NONE';
  }

  public generateDaysHeaderObjects(firstDay: Date, daysMode: DaysModeEnum): Date[] {
    const repeatValue = PeriodsOffsetMap[daysMode];
    return Array.from({ length: repeatValue }, (_, index) => addDays(firstDay, index));
  }

  public updateRoutesInPlan(driverRows: GridRowModel[], routesToReload: PlannerRouteDto[], plannerConfiguration: PlannerConfiguration): GridRowModel[] {
    this.checkIfCollectionLoaded();
    this.checkIfInitCompleted();

    const existingIds = new Set(this.theMostUpToDateLastRoutes!.map(route => route.Id));
    const newRoutes = routesToReload.filter(route => !existingIds.has(route.Id));

    const routesMap = new Map(routesToReload.map(route => [route.Id, route]));
    const updatedRoutes = this.theMostUpToDateLastRoutes!.map(route =>
      routesMap.has(route.Id) ? routesMap.get(route.Id)! : route);

    this.theMostUpToDateLastRoutes = [...updatedRoutes, ...newRoutes];
    this.theMostUpToDateLastRoutes = this.theMostUpToDateLastRoutes.filter(r => r.Orders.length > 0);

    let newRows = this.generateRoutesForDrivers(driverRows, this.theMostUpToDateLastRoutes, plannerConfiguration);
    newRows = this.mergeAbsencesResponse(newRows, this.theMostUpToDateLastAbsences!, this.theMostUpToDateLastCalendarsEvents!);
    return newRows;
  }

  private checkIfCollectionLoaded() {
    if (!this.theMostUpToDateLastRoutes) {
      throw 'theMostUpToDateLastRoutes is empty';
    }
    if (!this.theMostUpToDateLastCalendarsEvents) {
      throw 'theMostUpToDateLastCalendarsEvents is empty';
    }
    if (!this.theMostUpToDateLastAbsences) {
      throw 'theMostUpToDateLastAbsences is empty';
    }
  }

  public generateRoutesForDrivers(driverRows: GridRowModel[], routes: PlannerRouteDto[], plannerConfiguration: PlannerConfiguration): GridRowModel[] {
    this.theMostUpToDateLastRoutes = routes;

    const clonedRows = cloneDeep(driverRows);
    const driversRoutes = routes;
    const displayedDays = this.generateDaysHeaderObjects(plannerConfiguration.firstDay, plannerConfiguration.daysMode);
    for (const gridRow of clonedRows) {
      const driverRoutesByDays = driversRoutes.filter(dr => dr.DriverId === gridRow.driverVehicleInfo.driverId && dr.VehicleId === gridRow.driverVehicleInfo.vehicleId);
      const assistantRoutesByDays = driversRoutes.filter(dr => dr.HelperDriverId === gridRow.driverVehicleInfo.driverId || dr.SecondDriverId === gridRow.driverVehicleInfo.driverId);

      gridRow.days = displayedDays.map(day => {
        const routesInDay = driverRoutesByDays.filter(r => isSameDay(day, r.PlanTime));

        const phantomRoutesInDay = driverRoutesByDays.filter(r => !isSameDay(day, r.PlanTime) && r.PlanTime < day
          && (r.PlanEndTime > day));

        const assistantRoutesInDay = assistantRoutesByDays.filter(r => isSameDay(day, r.PlanTime));

        const assistantPhantomRoutesInDay = assistantRoutesByDays.filter(r => !isSameDay(day, r.PlanTime) && r.PlanTime < day
          && (r.PlanEndTime > day));

        const newDay = this.generateDayFromDriverRoutes(day, routesInDay, phantomRoutesInDay, assistantRoutesInDay, assistantPhantomRoutesInDay);
        return newDay;
      });

      this.checkIfRowExceededDesigatedHeight(gridRow);
    }

    return clonedRows;
  }

  private generateDayFromDriverRoutes(day: Date, routesInDay?: PlannerRouteDto[], phantomRoutes?: PlannerRouteDto[], driverAssistantRoutes?: PlannerRouteDto[], assistantPhantomRoutesInDay?: PlannerRouteDto[]): DayModel {
    this.checkIfInitCompleted();

    let routesCombined = routesInDay && routesInDay.length === 0 ? []
      : routesInDay!.sort(this.compareRouteByOrdersDate)
        .filter(r => r.Orders.length > 0).map(r => {
          let modifiedOrders = r.Orders.filter(o => isBeforeDay(day, new Date(o.StartDate)) && isAfterDay(day, new Date(o.EndDate)));
          if (modifiedOrders.length === 0) {
            modifiedOrders = r.Orders.slice(0, 1);
          }
          return mapRouteDtoToModel({ ...r, Orders: modifiedOrders }, this.taskOrderTypeSet!, this.orderStatusSet!);
        });

    const phantomRoutesModels = phantomRoutes && phantomRoutes.length === 0 ? []
      : phantomRoutes!.sort(this.compareRouteByOrdersDate)
        .filter(r => r.Orders.length > 0).map(r => {
          let modifiedOrders = r.Orders.filter(o => isBeforeDay(day, new Date(o.StartDate)) && isAfterDay(day, new Date(o.EndDate)));
          if (modifiedOrders.length === 0) {
            modifiedOrders = r.Orders.slice(0, 1);
          }
          return mapRouteDtoToModel({ ...r, Orders: modifiedOrders }, this.taskOrderTypeSet!, this.orderStatusSet!);
        })
        .map(r => ({ ...r, IsPhantomRoute: true } as RouteModel));

    const driverAssistantRoutesModels = driverAssistantRoutes && driverAssistantRoutes.length === 0 ? []
      : driverAssistantRoutes!.sort(this.compareRouteByOrdersDate)
        .filter(r => r.Orders.length > 0).map(r => { 
          let modifiedOrders = r.Orders.filter(o => isBeforeDay(day, new Date(o.StartDate)) && isAfterDay(day, new Date(o.EndDate)));
          if (modifiedOrders.length === 0) {
            modifiedOrders = r.Orders.slice(0, 1);
          }
          return mapRouteDtoToModel({ ...r, Orders: modifiedOrders }, this.taskOrderTypeSet!, this.orderStatusSet!)
        })
        .map(r => ({ ...r, IsRouteAsDriverAsistant: true } as RouteModel));

    const driverAssistantPhantomRoutesModels = assistantPhantomRoutesInDay && assistantPhantomRoutesInDay.length === 0 ? []
      : assistantPhantomRoutesInDay!.sort(this.compareRouteByOrdersDate)
        .filter(r => r.Orders.length > 0).map(r => {
          let modifiedOrders = r.Orders.filter(o => isBeforeDay(day, new Date(o.StartDate)) && isAfterDay(day, new Date(o.EndDate)));
          if (modifiedOrders.length === 0) {
            modifiedOrders = r.Orders.slice(0, 1);
          }
          return mapRouteDtoToModel({ ...r, Orders: modifiedOrders }, this.taskOrderTypeSet!, this.orderStatusSet!)
        })
        .map(r => ({ ...r, IsRouteAsDriverAsistant: true, IsPhantomRoute: true } as RouteModel));

    //the order in which activities are performed is important from a business point of view
    if (phantomRoutesModels.length) {
      routesCombined = [...phantomRoutesModels, ...routesCombined];
    }

    if (driverAssistantPhantomRoutesModels.length) {
      routesCombined = [...routesCombined, ...driverAssistantPhantomRoutesModels];
    }

    if (driverAssistantRoutesModels.length) {
      routesCombined = [...routesCombined, ...driverAssistantRoutesModels];
    }
    //

    const hasAnyRoutesAndOrders = routesCombined.length > 0 && routesCombined.flatMap(r => r.orders).length > 0;
    const dayModel: DayModel = {
      id: PlannerViewComponent.SharedIdSeq++,
      type: hasAnyRoutesAndOrders ? 'Routes' : 'Empty',
      isInvalid: false,
      isDayExpanded: true,
      dayExceededAllowableHeight: !!routesInDay && routesInDay.length > 1,
      routes: routesCombined,
      date: day
    };

    return dayModel;
  }

  private compareRouteByOrdersDate(baseRoute: PlannerRouteDto, route: PlannerRouteDto): any {
    return Math.min(...baseRoute.Orders.map(o => new Date(o.StartDate).getTime())) - Math.min(...route.Orders.map(o => new Date(o.StartDate).getTime()));
  }

  public checkIfRowExceededDesigatedHeight(row: GridRowModel): void {
    this.checkExceededDesignedHeightForDays(row.days);
    row.rowExceededAllowableHeight = row.days.some(d => d.dayExceededAllowableHeight);
    row.isRowExpanded = row.rowExceededAllowableHeight;
  }

  public mergeAbsencesResponse(gridRows: GridRowModel[], absencesResponse: ScheduleAbsenceDto[], calendarsResponse: EventDto[]): GridRowModel[] {
    this.theMostUpToDateLastAbsences = absencesResponse;
    this.theMostUpToDateLastCalendarsEvents = calendarsResponse;

    const clonedRows = cloneDeep(gridRows);

    for (const absence of absencesResponse) {
      if (absence.ObjectType === BusinessObjectType.DRIVER) {
        const rowsToApplyAbsence = clonedRows.filter(gr => gr.driverVehicleInfo.driverId === absence.ObjectId);

        this.applyAbsenceToRows(rowsToApplyAbsence, absence);
      } else if (absence.ObjectType === BusinessObjectType.VEHICLE) {
        const rowsToApplyAbsence = clonedRows.filter(gr => gr.driverVehicleInfo.vehicleId === absence.ObjectId || gr.driverVehicleInfo.trailerId === absence.ObjectId);

        this.applyAbsenceToRows(rowsToApplyAbsence, absence);
      }
    }

    for (const event of calendarsResponse) {
      const rowsToApplyAbsence = clonedRows.filter(gr => gr.driverVehicleInfo.vehicleId === event.ObjectId);
      this.applyObjectEventsToRows(rowsToApplyAbsence, event);
    }

    return clonedRows;
  }

  public applyObjectEventsToRows(rowsToApplyAbsence: GridRowModel[], event: EventDto) {
    for (const gridRow of rowsToApplyAbsence) {
      const daysToApplyAbsence = gridRow.days.filter(day => isSameDay(day.date, event.EventDate));

      let objectName = '';
      if (gridRow.driverVehicleInfo.vehicleId === event.ObjectId) {
        objectName = gridRow.driverVehicleInfo.model;
      }

      if (gridRow.driverVehicleInfo.trailerId === event.ObjectId) {
        objectName = gridRow.driverVehicleInfo.trailerModel;
      }

      const absenceModel = mapCalendarEventDtoToModel(event, objectName);
      for (const day of daysToApplyAbsence) {
        if (!day.absences) {
          day.absences = [];
        }

        day.absences = [...day.absences, absenceModel];

        if (day.type === 'Empty') {
          day.type = 'Routes';
        } else if (day.routes?.length > 0) {
          day.isInvalid = true;
        }
      }
    }
  }

  public applyAbsenceToRows(rowsToApplyAbsence: GridRowModel[], absence: ScheduleAbsenceDto, objectName = '', scheduleType = '') {
    const absenceModel = mapScheduleAbsenceDtoToModel(absence, objectName, scheduleType);
    for (const gridRow of rowsToApplyAbsence) {
      const daysToApplyAbsence = gridRow.days.filter(day => (day.date >= absence.DateStart && day.date < absence.DateEnd)
        || isSameDay(day.date, absence.DateStart)
        || isSameDay(day.date, absence.DateEnd));

      for (const day of daysToApplyAbsence) {
        if (!day.absences) {
          day.absences = [];
        }
        day.absences = [...day.absences, absenceModel];

        if (day.type === 'Empty') {
          day.type = 'Routes';
        } else if (day.routes?.length > 0) {
          day.isInvalid = true;
        }
      }
    }
  }

  public updateAbsenceListWithNewlyAddedAbsence(absence: ScheduleAbsenceDto) {
    if (this.theMostUpToDateLastAbsences) {
      this.theMostUpToDateLastAbsences = [...this.theMostUpToDateLastAbsences, absence];
    }
  }

  private checkExceededDesignedHeightForDays(days: DayModel[]): void {
    if (days) {
      for (const day of days) {
        if (day.type === 'Routes') {

          const multipleRoutesInOneDay = day.routes?.length > 1;
          const multipleOrdersInOneRoute = multipleRoutesInOneDay ? null
            : day.routes?.filter(r => r).some(r => r.orders?.length > 1);

          day.dayExceededAllowableHeight = multipleRoutesInOneDay || multipleOrdersInOneRoute === true;
          day.isDayExpanded = day.dayExceededAllowableHeight;

        } else {
          day.dayExceededAllowableHeight = false;
          day.isDayExpanded = false;
        }
      }
    }
  }

  private checkIfInitCompleted(): void {
    if (!this.vehicleCategorySet) {
      throw 'no vehicleCategorySet';
    }

    if (!this.orderStatusSet) {
      throw 'no orderStatusSet';
    }

    if (!this.taskOrderTypeSet) {
      throw 'no taskOrderTypeSet';
    }
  };
}
